/**
 * «Пришёл / Не пришёл» with a 5-second «Отменить» (concepts/today-approved.html, rule 2).
 *
 * The server keeps completed and no_show final (app/domain/appointments.py,
 * "nothing reopens one"), so undo never reaches it: a mark shows at once and
 * the PATCH goes out only when the undo bar closes. «Отменить» drops the mark
 * before anything is sent. A new tap, leaving the screen or the app going to
 * the background sends the waiting mark straight away. A failed save takes the
 * mark back off the screen and says why in a toast.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { toast } from '../../components/Toast';
import { ApiError, api } from '../../core/api';
import { errorText } from '../../i18n';
import type { Mark, Marks } from './model';

export const UNDO_MS = 5_000;

interface Waiting {
  ids: string[];
  mark: Mark;
  /** What the undo bar says: «Гүлназ: Пришёл», «3 визита: Пришли». */
  label: string;
}

export interface MarksState {
  marks: Marks;
  /** The undo bar's text while a mark waits; null when nothing waits. */
  undoLabel: string | null;
  mark: (ids: string[], mark: Mark, label: string) => void;
  undo: () => void;
}

export function useMarks(): MarksState {
  const qc = useQueryClient();
  const [marks, setMarks] = useState<Marks>({});
  const [waiting, setWaiting] = useState<Waiting | null>(null);
  const waitingRef = useRef<Waiting | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const drop = useCallback((ids: string[]) => {
    setMarks((m) => {
      const next = { ...m };
      for (const id of ids) delete next[id];
      return next;
    });
  }, []);

  const send = useCallback(
    async (w: Waiting) => {
      try {
        await Promise.all(w.ids.map((id) => api.updateAppointment(id, { status: w.mark })));
      } catch (err) {
        toast(errorText(err instanceof ApiError ? err.code : 'fallback'));
      }
      // Keep the mark on screen until the server's own answer has arrived, so the card never flickers back.
      await Promise.all(
        ['today', 'day', 'week', 'month', 'appointment'].map((key) => qc.invalidateQueries({ queryKey: [key] })),
      );
      drop(w.ids);
    },
    [qc, drop],
  );

  const flush = useCallback(() => {
    clearTimeout(timer.current);
    const w = waitingRef.current;
    if (!w) return;
    waitingRef.current = null;
    setWaiting(null);
    void send(w);
  }, [send]);

  const mark = useCallback(
    (ids: string[], m: Mark, label: string) => {
      if (!ids.length) return;
      flush();
      setMarks((cur) => ({ ...cur, ...Object.fromEntries(ids.map((id) => [id, m])) }));
      const w = { ids, mark: m, label };
      waitingRef.current = w;
      setWaiting(w);
      timer.current = setTimeout(flush, UNDO_MS);
    },
    [flush],
  );

  const undo = useCallback(() => {
    clearTimeout(timer.current);
    const w = waitingRef.current;
    if (!w) return;
    waitingRef.current = null;
    setWaiting(null);
    drop(w.ids);
  }, [drop]);

  useEffect(() => {
    const hidden = () => document.visibilityState === 'hidden' && flush();
    document.addEventListener('visibilitychange', hidden);
    return () => {
      document.removeEventListener('visibilitychange', hidden);
      flush();
    };
  }, [flush]);

  return { marks, undoLabel: waiting?.label ?? null, mark, undo };
}
